<template>
  <zt-data-table
    :columns="columns"
    :rows="rows"
    :config="{ pagination: { use: false } }"
    :loading="loading"
  >
    <template v-slot:header>
      <div class="px-6 py-4 font-bold border-b bg-zt-white">
        <h2 class="text-zt-xl table-title">
          {{ $t('message.workflow.table.title') }}
        </h2>
      </div>
    </template>
    <template v-slot:created_at="{ item }">
      <div class="flex justify-start items-center">
        <span class="ml-2 flex flex-wrap">
          {{ item.created_at }}
        </span>
      </div>
    </template>
    <template v-slot:applicant="{ item }">
      <div class="flex justify-start items-center">
        <zt-avatar :text="item.fullname" color="secondary-100" />
        <span class="ml-2 flex flex-wrap">
          {{ item.fullname }}
        </span>
      </div>
    </template>
    <template v-slot:document_type="{ item }">
      <div class="flex items-center mb-2">
        <zt-icon name="file-text-outline" class="mr-2 doc-name" />
        <span class="truncate block">
          {{ item.document_type.name }}
        </span>
      </div>
      <div class="flex items-center">
        <zt-chip square outline class="workflow-chip uppercase">
          {{ $t('message.workflow.table.columns.chip') }}
        </zt-chip>
        <ul class="workflow-state ml-2">
          <li
            v-for="(approve, index) in item.approves"
            :key="approve.id"
            class="mr-1"
          >
            <zt-tooltip position="right">
              <template slot="body">
                <div class="flex flex-col">
                  <span class="uppercase">{{ approve.group.name }}</span>
                  <span v-if="approve.status.id === 2">
                    {{ $t('message.workflow.table.approveBy') }}
                    {{ approve.fullname }}
                  </span>
                  <span v-if="approve.status.id === 3">
                    {{ $t('message.workflow.table.rejectedBy') }}
                    {{ approve.fullname }}
                  </span>
                  <span
                    v-if="
                      approve.status.id === 3 ||
                        (approve.status.id === 2 && approve.reviewed_at)
                    "
                  >
                    {{ $t('message.workflow.table.atDay') }}
                    {{ getDate(approve.reviewed_at) }}
                  </span>
                  <span v-if="approve.comment" class="mt-2">
                    {{ $t('message.workflow.table.comment') }}
                    {{ approve.comment }}
                  </span>
                </div>
              </template>
              <div
                class="flex items-center justify-center select-none"
                :class="getCircleStatus(approve, item.status)"
              >
                {{ index + 1 }}
              </div>
            </zt-tooltip>
          </li>
        </ul>
      </div>
    </template>
    <template v-slot:status="{ item }">
      <div class="flex items-center">
        <zt-chip square outline :class="getChipClass(item)">
          {{ getStatus(item.status) }}
        </zt-chip>
      </div>
    </template>
    <template v-slot:id="{ item }">
      <div class="flex justify-center items-center">
        <zt-button
          outlined
          class="button-table-action"
          data-vs-button="btn-donwload-history-item"
          @click="$emit('workflow-selected', item)"
        >
          <div class="flex items-center">
            <zt-icon name="eye" class="mr-2" />
            <div>
              {{ $t('message.workflow.table.seeDocument') }}
            </div>
          </div>
        </zt-button>
      </div>
    </template>
  </zt-data-table>
</template>

<script>
import moment from 'moment'

export default {
  name: 'WorkflowRequestDesktop',
  props: {
    rows: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    columns() {
      return [
        {
          name: this.$t('message.workflow.table.columns.date'),
          dataField: 'created_at',
          width: 140
        },
        {
          name: this.$t('message.workflow.table.columns.applicant'),
          dataField: 'applicant'
        },
        {
          name: this.$t('message.workflow.table.columns.documentType'),
          dataField: 'document_type'
        },
        {
          name: this.$t('message.workflow.table.columns.status'),
          dataField: 'status'
        },
        {
          name: this.$t('message.workflow.table.columns.actions'),
          dataField: 'id',
          width: 190
        }
      ]
    }
  },
  methods: {
    getDate(date) {
      if (!date) return ''
      const formattedDate = moment(date, 'DD/MM/YYYY HH:mm:ss.SSSSSS')
      return formattedDate.format('DD/MM/YYYY')
    },
    getStatus(status) {
      const obj = {
        1: this.$t('message.workflow.status.coursed'),
        2: this.$t('message.workflow.status.approved'),
        3: this.$t('message.workflow.status.rejected'),
        4: this.$t('message.workflow.status.new')
      }
      return obj[status]
    },
    getCircleStatus(approve, workflowStatus) {
      const statusMap = {
        1: 'pending',
        2: 'approve',
        3: 'cancelled'
      }
      if (approve.status.id === 0 && workflowStatus === 1) {
        return 'pending'
      }
      return statusMap[approve.status.id] || {}
    },
    getChipClass(item) {
      const classMap = {
        1: 'workflow-chip-coursed',
        2: 'workflow-chip-success',
        3: 'workflow-chip-rejected'
      }
      return classMap[item.status] || {}
    }
  }
}
</script>

<style scoped lang="scss">
.table-title {
  color: #2d2d2d;
}

.workflow-state {
  li {
    display: inline-block;
    vertical-align: center;
    div {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: #707070;
      font-size: 12px;
      color: #fff;
    }
    div.approve {
      background: #7faf7f;
    }
    div.cancelled {
      background: #f16b6c;
    }
    div.pending {
      background: #fb8c00;
    }
    div.new {
      background: #cbf7e8;
    }    
  }
}

::v-deep {
  .zt-data-table {
    thead,
    tbody,
    tfoot {
      background: #fff !important;
    }
    th {
      font-weight: 500 !important;
    }
    thead {
      div {
        color: #2d2d2d;
      }
    }
    tbody {
      .doc-name.zt-icon {
        font-size: 1.5rem;
      }
    }
  }
  .button-table-action.zt-button {
    background: #fff !important;
    color: #707070 !important;
    border-color: #707070 !important;
    .zt-icon {
      font-size: 1rem;
    }
  }
  .zt-chip {
    min-width: 140px;
    justify-content: center;
    margin: 0;
  }
  .workflow-chip {
    background: #eae8f4 !important;
    color: #4f41a3;
  }
  .workflow-chip-coursed {
    background: #e1f5fe !important;
    color: #017eec;
  }
  .workflow-chip-rejected {
    background: #ffd4d8 !important;
    color: #f16b6c;
  }
  .workflow-chip-success {
    background: #e5fff7 !important;
    color: #54caa6;
  }
}
</style>